/**
 * E-Mail-Vorlagen beim Schreiben – eine Stelle für „Neue Nachricht“ und das
 * Antworten (Kundenfeedback 25.10.2026 Teil 3, I): Platzhalter werden beim
 * Antworten genauso aufgelöst wie beim Neu-Erstellen, die Vorauswahl „An
 * wen?“ im Vorlagen-Fenster folgt derselben Regel.
 *
 * Reine Funktionen ohne Vue/Store.
 */
import type { InsuranceLookup, InvolvedLike } from '@/utils/mailRecipientCandidates'
import { buildRecipientCandidates } from '@/utils/mailRecipientCandidates'
import {
  buildTemplateValues,
  type MailTemplateValues,
  renderMailTemplateHtml,
  renderMailTemplateSubject,
  type TemplateLocationInfo,
  type TemplateUserInfo,
  unresolvedPlaceholders,
} from '@/utils/mailTemplate'
import { categoryForRecipient } from '@/utils/mailTemplateFilter'

type Records = Array<Record<string, unknown>>

/** Falldaten, aus denen Vorlagen-Platzhalter befüllt werden. */
export interface ComposeCaseData {
  caseNumber?: string | null
  caseType?: string | null
  clients?: Records | null
  vehicles?: Records | null
  opponents?: Records | null
  accidents?: Records | null
  involved?: InvolvedLike[] | null
}

/** Absender-Angaben aus der aufgelösten Signatur (Unternehmen, Webseite, Standort). */
export interface ComposeSenderInfo {
  user: TemplateUserInfo
  companyName?: string | null
  orgName?: string | null
  website?: string | null
  orgHomepage?: string | null
  location?: TemplateLocationInfo | null
}

/**
 * Platzhalter-Werte: angemeldeter Nutzer, Unternehmen/Standort der Signatur
 * und die Akte. Unternehmen und Webseite kommen – wie in der Signatur – vom
 * Signatur-Standort, ersatzweise von der Organisation.
 */
export function composeTemplateValues (sender: ComposeSenderInfo, caseData: ComposeCaseData | null): MailTemplateValues {
  return buildTemplateValues({
    user: {
      ...sender.user,
      organizationName: sender.companyName || sender.orgName || sender.user.organizationName,
      website: sender.website || sender.orgHomepage || sender.user.website,
    },
    location: sender.location ?? null,
    case: caseData
      ? {
          caseNumber: caseData.caseNumber,
          clients: caseData.clients,
          vehicles: caseData.vehicles,
          opponents: caseData.opponents,
          accidents: caseData.accidents,
          involved: caseData.involved,
        }
      : null,
  })
}

export interface TemplateLike {
  name: string
  subject?: string | null
  bodyHtml?: string | null
}

export interface RenderedTemplate {
  name: string
  /** Betreff der Vorlage (leer, wenn sie keinen hat). */
  subject: string
  /** Text mit befüllten Platzhaltern; nicht befüllbare sind gelb markiert. */
  html: string
  /** Anzahl nicht befüllbarer Platzhalter. */
  missing: number
}

export function renderTemplateForInsert (tpl: TemplateLike, values: MailTemplateValues): RenderedTemplate {
  const subject = tpl.subject || ''
  const body = tpl.bodyHtml || ''
  return {
    name: tpl.name,
    subject: renderMailTemplateSubject(subject, values),
    html: renderMailTemplateHtml(body, values, { unresolved: 'mark' }),
    missing: unresolvedPlaceholders(`${subject} ${body}`, values).length,
  }
}

/**
 * Empfänger-Vorauswahl im Vorlagen-Fenster (Kundenfeedback 25.10.2026
 * Teil 2, II): die Adresse wird mit den Beteiligten der Akte abgeglichen –
 * Kunde → „Mandant / Kunde“, Versicherung → „Versicherung“ usw. Ohne
 * Treffer bleibt „Alle“ (leerer Schlüssel).
 */
export function suggestTemplateCategory (
  email: string | null | undefined,
  caseData: ComposeCaseData | null,
  lookup: InsuranceLookup | null = null,
): string {
  const address = String(email || '').trim().toLowerCase()
  if (!address || !caseData) {
    return ''
  }
  const candidates = buildRecipientCandidates(
    (caseData.involved || []).filter(p => !p.removedAt),
    {
      opponents: caseData.opponents,
      clients: caseData.clients,
      vehicles: caseData.vehicles,
      isKaskoCase: String(caseData.caseType || '').toLowerCase() === 'kasko',
      lookup,
    },
  )
  return categoryForRecipient(candidates.find(c => c.email === address))
}
